/**
 * @fileoverview Catálogo da Área de Treinamento (consume). Grid de cards estilo
 * Udemy: thumbnail, título, descrição, nº de aulas e barra de progresso. Gate por
 * `enableAcademy` no mount + listener de socket (expulsa quem está na página se
 * o admin desligar ao vivo).
 */

import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Paper, Typography, LinearProgress } from "@material-ui/core";
import { GraduationCap } from "lucide-react";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import useAcademy from "../../hooks/useAcademy";
import useSettings from "../../hooks/useSettings";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1e293b",
  },
  grid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: theme.spacing(3),
    padding: 0,
  },
  card: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.spacing(1),
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: theme.shadows[2],
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[6],
    },
  },
  thumb: {
    width: "100%",
    aspectRatio: "16 / 9",
    objectFit: "cover",
    backgroundColor: theme.palette.action.hover,
  },
  thumbPlaceholder: {
    width: "100%",
    aspectRatio: "16 / 9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
  body: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    flex: 1,
  },
  cardTitle: {
    fontWeight: 600,
  },
  description: {
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  meta: {
    marginTop: "auto",
    paddingTop: theme.spacing(1),
  },
  progress: {
    height: 6,
    borderRadius: 3,
    marginTop: theme.spacing(0.5),
  },
  empty: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const Academy = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const { listCourses } = useAcademy();
  const { getAll } = useSettings();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const redirectDisabled = useCallback(() => {
    toast.info(i18n.t("academy.toasts.disabled"));
    history.push("/");
  }, [history]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Gate: protege acesso por URL direta. Default-ligado (só "disabled" esconde).
        const settings = await getAll();
        const academySetting = Array.isArray(settings)
          ? settings.find(s => s.key === "enableAcademy")
          : null;
        if (academySetting?.value === "disabled") {
          if (active) redirectDisabled();
          return;
        }
        const data = await listCourses();
        if (active) setCourses(data || []);
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disable ao vivo: se o admin desligar, expulsa quem está na página
  useEffect(() => {
    if (!user?.companyId) return;
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);
    const handler = data => {
      if (
        data.action === "update" &&
        data.setting?.key === "enableAcademy" &&
        data.setting?.value === "disabled"
      ) {
        redirectDisabled();
      }
    };
    socket.on(`company-${companyId}-settings`, handler);
    return () => {
      socket.off(`company-${companyId}-settings`, handler);
    };
  }, [socketManager, user?.companyId, redirectDisabled]);

  const renderCard = course => {
    const total = course.lessonsCount || 0;
    const completed = course.completedCount || 0;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

    return (
      <Paper
        key={course.id}
        className={classes.card}
        onClick={() => history.push(`/academy/courses/${course.id}`)}
      >
        {course.thumbnailPath ? (
          <img
            className={classes.thumb}
            src={`${backendUrl}/public/${course.thumbnailPath}`}
            alt={course.title}
          />
        ) : (
          <div className={classes.thumbPlaceholder}>
            <GraduationCap size={48} strokeWidth={1.2} />
          </div>
        )}
        <div className={classes.body}>
          <Typography variant="subtitle1" className={classes.cardTitle}>
            {course.title}
          </Typography>
          {course.description && (
            <Typography variant="body2" className={classes.description}>
              {course.description}
            </Typography>
          )}
          <div className={classes.meta}>
            <Typography variant="caption" color="textSecondary">
              {total} {i18n.t("academy.lessons")} · {pct}% {i18n.t("academy.progress")}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              className={classes.progress}
            />
          </div>
        </div>
      </Paper>
    );
  };

  return (
    <MainContainer className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>{i18n.t("academy.title")}</Typography>
      </div>
      {!loading && courses.length === 0 ? (
        <div className={classes.empty}>
          <GraduationCap size={64} strokeWidth={1} />
          <Typography variant="h6">{i18n.t("academy.empty")}</Typography>
        </div>
      ) : (
        <div className={classes.grid}>{courses.map(renderCard)}</div>
      )}
    </MainContainer>
  );
};

export default Academy;
